import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import axios from "axios";
import * as XLSX from "xlsx";
import { FaAngleLeft } from "react-icons/fa";
import ProfilePic from "../../../assets/images/dummy_profile.png";
import Loader from "../../../components/Loader/Loader";
import "../../../components/common/css/campaignview.scss";
import "../../new/new.scss";
import Logo from "../../../assets/logo/logo-image.svg";
import { FaCopy } from "react-icons/fa";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import { useCountries, bankCountryLabel } from "../../../utils/bankDisplay";

/**
 * How this order was paid, for the invoice.
 *
 * Reads the order's own campaign_investments.paymentOption. That column is
 * written server-side by paymentRouting.legacyPaymentOption ("Wire Transfer" /
 * "Direct Deposit") and by the AD manual-payment console ("MANUAL_BANK");
 * "E Transfer" is legacy data with no current writer.
 *
 * This replaces a useState("Wire Transfer") whose setter was never called, so
 * the invoice claimed Wire Transfer for every order however it was actually
 * paid, and the other two branches were unreachable.
 *
 * Unknown / null is stated plainly rather than guessed: paymentOption is NULL
 * until the investor reaches the payment step, and an invoice that names the
 * wrong rail is worse than one that names none.
 */
const paymentMethodLabel = (paymentOption) => {
  switch (paymentOption) {
    case "Wire Transfer":
      return "Payment method: wire transfer.";
    case "Direct Deposit":
      return "Payment method: pre-authorized debit from the investor's bank account.";
    case "E Transfer":
      return "Payment method: e-transfer.";
    case "MANUAL_BANK":
      return "Payment method: bank transfer, confirmed manually by an administrator.";
    default:
      return "Payment method: not selected yet.";
  }
};

const Viewinvestment = () => {
  const navigate = useNavigate();
  const id = useParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [share, setShare] = useState();
  const [paymentStatus, setPaymentStatus] = useState("");
  const countries = useCountries();
  const [isPaymentStatusChanged, setIsPaymentStatusChanged] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isPdfMode, setIsPdfMode] = useState(false); // Add this state

  var token = localStorage.getItem("accessToken");
  const formattedId = `OR${String(data?.id || "").padStart(6, "0")}`;
  const equitymessage = () => {
    const eqval1 = data?.amount;
    const eqval2 = data?.campaign_detail?.price_per_security;
    if (eqval1) {
      const percentage = eqval1 / eqval2;
      setShare(
        `Purchasing shares of  ${
          percentage == "Infinity" ? "N/A" : Math.round(percentage)
        } `
      );
    } else {
      setShare("");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const onSubmit = async () => {
    try {
      // ✅ Check condition before proceeding
      if (
        data?.investmentAccount === "CORPORATE" &&
        data?.user?.comp_isVerified === false
      ) {
        Swal.fire({
          title: "Error",
          text: "Please verify the corporate details before updating the payment status.",
          icon: "error",
          confirmButtonText: "OK",
        });
        return; // Stop the execution here
      }
      setLoading(true);
      // Generate and upload PDF
      const pdfUrl = await generatePdfAndUpload();
      if (!pdfUrl) {
        setLoading(false);
        Swal.fire({
          title: "Error",
          text: "Failed to generate or upload the PDF.",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      let payload = {
        id: data?.id,
        invoice_pdf_url: pdfUrl,
        paymentStatus: paymentStatus,
      };
      var config = {
        method: "put",
        url: process.env.REACT_APP_API + "admin/update-investment",
        headers: {
          "x-access-token": token,
        },
        data: payload,
      };
      axios(config).then(function (response) {
        if (response?.data?.status === 200) {
          setLoading(false);
          Swal.fire({
            title: "Success",
            timer: 2500,
            timerProgressBar: true,
            text: response?.data?.message,
            icon: "success",
            confirmButtonText: "OK",
            showCloseButton: true,
          })
          navigate("/investment")
        }
      });
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error.response?.data?.message || "An error occurred.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchjobData = () => {
    const token = localStorage.getItem("accessToken");
    setLoading(true);
    var config = {
      method: "post",
      url: process.env.REACT_APP_API + "admin/get-myinvestment-details-by-id",
      headers: {
        "x-access-token": token,
      },
      data: {
        id: id.investmentId,
      },
    };

    axios(config)
      .then(function (response) {
        setLoading(false);
        const fetchedData = response.data.data.rows[0];
        setData(fetchedData);
        setPaymentStatus(fetchedData.paymentStatus);
        setIsPaymentStatusChanged(false);
      })
      .catch(function (error) {
        setLoading(false);
        Swal.fire({
          title: error?.response?.data?.message || "An error occurred",
          timerProgressBar: true,
          icon: "error",
          confirmButtonText: "OK",
          showCloseButton: true,
        });
      });
  };

  useEffect(() => {
    fetchjobData();
  }, []);

  useEffect(() => {
    if (data) {
      equitymessage();
    }
  }, [data]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB");
  };

  // const generatePdfAndUpload = async () => {
  //   try {
  //     const section = document.querySelector(".order-confirmation");
  //     if (!section) {
  //       console.error("No element with the class 'order-confirmation' found.");
  //       return null;
  //     }

  //     const canvas = await html2canvas(section, {
  //       scale: 1.5,
  //       useCORS: true,
  //     });

  //     const imgData = canvas.toDataURL("image/jpeg", 0.7);
  //     const pdf = new jsPDF("p", "mm", "a4");
  //     const pdfWidth = pdf.internal.pageSize.getWidth();
  //     const imgHeight = (canvas.height * pdfWidth) / canvas.width;

  //     let position = 0;
  //     pdf.addImage(imgData, "JPEG", 0, position, pdfWidth, imgHeight);

  //     let heightLeft = imgHeight - pdf.internal.pageSize.getHeight();
  //     while (heightLeft > 0) {
  //       position -= pdf.internal.pageSize.getHeight();
  //       pdf.addPage();
  //       pdf.addImage(imgData, "JPEG", 0, position, pdfWidth, imgHeight);
  //       heightLeft -= pdf.internal.pageSize.getHeight();
  //     }

  //     const pdfBlob = pdf.output("blob");

  //     const formData = new FormData();
  //     formData.append("media", pdfBlob, "order_confirmation.pdf");

  //     const response = await axios.post(
  //       process.env.REACT_APP_API + "utils/upload-file",
  //       formData,
  //       {
  //         headers: {
  //           "Content-Type": "multipart/form-data",
  //           "x-access-token": token,
  //         },
  //       }
  //     );

  //     if (response.status === 200 && response.data?.document_name) {
  //       console.log("PDF uploaded successfully:", response.data.document_name);
  //       return response.data.document_name; // Return the uploaded PDF URL
  //     } else {
  //       console.error("Failed to upload PDF. Response:", response.data);
  //       return null;
  //     }
  //   } catch (error) {
  //     console.error("Error generating or uploading PDF:", error);
  //     return null;
  //   }
  // };

  const generatePdfAndUpload = async () => {
    try {
      setIsPdfMode(true);
      setIsGeneratingPDF(true);

      await new Promise((resolve) => setTimeout(resolve, 100));

      const section = document.querySelector(".order-confirmation");
      if (!section) {
        console.error("No element with the class 'order-confirmation' found.");
        return null;
      }

      const canvas = await html2canvas(section, { scale: 1.5, useCORS: true });
      const imgData = canvas.toDataURL("image/jpeg", 0.7);
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      let position = 0;
      pdf.addImage(imgData, "JPEG", 0, position, pdfWidth, imgHeight);

      let heightLeft = imgHeight - pdf.internal.pageSize.getHeight();
      while (heightLeft > 0) {
        position -= pdf.internal.pageSize.getHeight();
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, pdfWidth, imgHeight);
        heightLeft -= pdf.internal.pageSize.getHeight();
      }

      const pdfBlob = pdf.output("blob");
      const formData = new FormData();
      formData.append("media", pdfBlob, "order_confirmation.pdf");

      const response = await axios.post(
        process.env.REACT_APP_API + "admin/utils/upload-file",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "x-access-token": token,
          },
        }
      );

      if (response.status === 200 && response.data?.document_name) {
        console.log("PDF uploaded successfully:", response.data.document_name);
        return response.data.document_name;
      } else {
        console.error("Failed to upload PDF. Response:", response.data);
        return null;
      }
    } catch (error) {
      console.error("Error generating or uploading PDF:", error);
      return null;
    } finally {
      setIsPdfMode(false);
      setIsGeneratingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    const token = localStorage.getItem("accessToken");
    var config = {
      method: "post",
      url: process.env.REACT_APP_API + "reports/transaction",
      headers: {
        "x-access-token": token,
      },
      data: {
        investmentId: data?.id,
      },
    };

    axios(config)
      .then(function (response) {
        if (response) {
          setLoading(false);
          var abc = base64ToBlob(response.data, "application/vnd.ms-excel");
          const url = window.URL.createObjectURL(abc);
          const link = document.createElement("a");
          link.href = url;
          const filename = getDateFormat(new Date()) + "_" + data?.id;
          link.setAttribute("download", `${filename}.xlsx`);
          document.body.appendChild(link);
          link.click();
        }
      })
      .catch(function (error) {
        setLoading(false);
        console.error("Export transaction error:", error);
      });
  };

  const exportCorporate = () => {
    const data1 = Array.isArray(data) ? data : [data];
    const dataToExport = data1.map((campaign) => ({
      ID: campaign?.campaign_detail?.id,
      "Campaign Name": campaign?.campaign_detail?.corporation_legal_name,
      "Official Email": campaign?.campaign_detail?.camp_email,
      "Company Address": campaign?.campaign_detail?.registered_address,
      City: campaign?.campaign_detail?.city,
      EnityType: campaign?.campaign_detail?.entitytype,
      "Buisness No": campaign?.campaign_detail?.business,
      "Craax Account": campaign?.campaign_detail?.craax_account,
      "Date Of Incorp": campaign?.campaign_detail?.date_of_incorp,
      "How many years in business":
        campaign?.campaign_detail?.how_many_yearsin_business,
      "Corporation Name": campaign?.user?.comp__name,
      "Investment Account": campaign?.investmentAccount,
      Amount: campaign?.amount,
      Currency: campaign?.currency,
      CustomerId: campaign?.user_id,
      OrderId: campaign?.order_id,
      "Bank Country": bankCountryLabel(
        countries,
        campaign?.user?.user_banking_details?.corporate_bank_country_id
      ),
      "Bank Name": campaign?.user?.user_banking_details?.corporate_bank_name,
      IBAN: campaign?.user?.user_banking_details?.corporate_iban,
      "SWIFT Code":
        campaign?.user?.user_banking_details?.corporate_swift_code,
      "Institutional Number":
        campaign?.user?.user_banking_details?.corporate_institution_number,
      "Transit Number":
        campaign?.user?.user_banking_details?.corporate_transit_number,
      "Account Number":
        campaign?.user?.user_banking_details?.corporate_account_number,
      "Account Holder Name":
        campaign?.user?.user_banking_details?.corporate_account_holder,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Selected Campaigns");
    XLSX.writeFile(workbook, "CampaignListing.xlsx");
  };

  const exportIndividual = () => {
    const data1 = Array.isArray(data) ? data : [data];
    const dataToExport = data1.map((campaign) => ({
      ID: campaign?.campaign_detail?.id,
      "Campaign Name": campaign?.campaign_detail?.corporation_legal_name,
      "Official Email": campaign?.campaign_detail?.camp_email,
      "Company Address": campaign?.campaign_detail?.registered_address,
      City: campaign?.campaign_detail?.city,
      EnityType: campaign?.campaign_detail?.entitytype,
      "Buisness No": campaign?.campaign_detail?.business,
      "Craax Account": campaign?.campaign_detail?.craax_account,
      "Date Of Incorp": campaign?.campaign_detail?.date_of_incorp,
      "How many years in business":
        campaign?.campaign_detail?.how_many_yearsin_business,
      "Investment Account": campaign?.investmentAccount,
      Firstname: campaign?.user?.firstname,
      Lastname: campaign?.user?.lastname,
      Amount: campaign?.amount,
      Currency: campaign?.currency,
      CustomerId: campaign?.user_id,
      OrderId: campaign?.order_id,
      "Bank Country": bankCountryLabel(
        countries,
        campaign?.user?.user_banking_details?.bank_country_id
      ),
      "Bank Name": campaign?.user?.user_banking_details?.bank_name,
      IBAN: campaign?.user?.user_banking_details?.iban,
      "SWIFT Code": campaign?.user?.user_banking_details?.swift_code,
      "Institutional Number":
        campaign?.user?.user_banking_details?.institution_number,
      "Transit Number": campaign?.user?.user_banking_details?.transit_number,
      "Account Number": campaign?.user?.user_banking_details?.account_number,
      "Account Holder Name":
        campaign?.user?.user_banking_details?.account_holder,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Selected Campaigns");
    XLSX.writeFile(workbook, "CampaignListing.xlsx");
  };

  const updateInvestment = async (newStatus) => {
    try {
      const token = localStorage.getItem("accessToken");
      await axios.put(
        process.env.REACT_APP_API + "admin/update-investment",
        { id: data?.id, paymentStatus: newStatus },
        {
          headers: {
            "x-access-token": token,
            "Content-Type": "application/json",
          },
        }
      );
      fetchjobData();
      Swal.fire({
        title: "Payment status updated successfully",
        icon: "success",
        confirmButtonText: "OK",
      }).then(() => {
        setIsPaymentStatusChanged(true);
      });
    } catch (error) {
      Swal.fire({
        title: "Error updating payment status",
        text: error.response?.data?.message || "Something went wrong",
        icon: "error",
      });
    }
  };

  const getDateFormat = (date) => {
    const today = date;
    const yyyy = today.getFullYear();
    let mm = today.getMonth() + 1; // Months start at 0!
    let dd = today.getDate();

    if (dd < 10) dd = "0" + dd;
    if (mm < 10) mm = "0" + mm;

    return yyyy + "" + mm + "" + dd;
  };

  const base64ToBlob = (base64Data, contentType) => {
    const sliceSize = 1024;
    const byteCharacters = atob(base64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);

      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  };

  return (
    <div className="new">
      <Loader isLoading={loading} />
      <div className="newContainer">
        <div className="top">
          <span className="backarrow">
            <FaAngleLeft onClick={() => navigate(-1)} />
          </span>
          <h1 className="view-page-tittle">Edit Investment</h1>
        </div>
        <PageShell><PageHead title="Investment edit" /><div className="single">
          <div className="singleContainer">
            <div className="top">
              <div
                className="left"
                style={{ textAlign: "left", boxShadow: "unset" }}
              >
                <div className="topHeading">
                  {" "}
                  <h1
                    className="title"
                    style={{ fontSize: "16px", color: "#333", width: "100%" }}
                  >
                    Corporate Information
                  </h1>
                  <div className="exportBtn-divSec">
                    <button
                      className="custBtn"
                      id="downloadbtn"
                      onClick={
                        data?.investmentAccount == "INDIVIDUAL"
                          ? exportIndividual
                          : exportCorporate
                      }
                    >
                      Export as Excel
                    </button>
                  </div>
                </div>

                <div className="item">
                  <div className="details profile-info">
                    <div className="detailItem">
                      <span className="itemKey">Company Name:</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.corporation_legal_name}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Company Email</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.camp_email}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Company Address:</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.registered_address}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Head Office Address</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.city}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Country</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.country_id}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">State/Province</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.province}
                      </span>
                    </div>

                    <div className="detailItem">
                      <span className="itemKey">Type Of Enitity:</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.entitytype}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Buisness No:</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.business}
                      </span>
                    </div>

                    <div className="detailItem">
                      <span className="itemKey">Fax</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.fax !== "undefined" &&
                        data?.campaign_detail?.fax !== "null"
                          ? data?.campaign_detail?.fax
                          : "-"}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Craax Acoount</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.craax_account !== "undefined" &&
                        data?.campaign_detail?.craax_account !== "null"
                          ? data?.campaign_detail?.craax_account
                          : "-"}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Date Of Corp</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.date_of_incorp}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Year Of Corporation</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.how_many_yearsin_business}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div></PageShell>
        <div className="single">
          <div className="singleContainer">
            <div className="top">
              <div
                className="left"
                style={{ textAlign: "left", boxShadow: "unset" }}
              >
                <div className="topHeading">
                  {" "}
                  <h1
                    className="title"
                    style={{ fontSize: "16px", color: "#333", width: "100%" }}
                  >
                    Personal Information
                  </h1>
                </div>

                {data?.investmentAccount === "INDIVIDUAL" ? (
                  <div className="item">
                    <div className="details profile-info">
                      <div className="detailItem">
                        <span className="itemKey">First Name</span>
                        <span className="itemValue">
                          {data?.user?.firstname}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Last Name</span>
                        <span className="itemValue">
                          {data?.user?.lastname}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Investment Account</span>
                        <span className="itemValue">
                          {data?.investmentAccount}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Campaign ID</span>
                        <span className="itemValue">{data?.campaign_id}</span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Campaign Name</span>
                        <span className="itemValue">
                          {data?.campaign_detail?.campaign_name}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Corporate Name</span>
                        <span className="itemValue">
                          {data?.campaign_detail?.corporation_legal_name}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Amount</span>
                        <span className="itemValue">{data?.amount}</span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Currency</span>
                        <span className="itemValue">{data?.currency}</span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Bank Country</span>
                        <span className="itemValue">
                          {bankCountryLabel(
                            countries,
                            data?.user?.user_banking_details?.bank_country_id
                          )}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Bank Name</span>
                        <span className="itemValue">
                          {data?.user?.user_banking_details?.bank_name}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">IBAN</span>
                        <span className="itemValue">
                          {data?.user?.user_banking_details?.iban}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">SWIFT Code</span>
                        <span className="itemValue">
                          {data?.user?.user_banking_details?.swift_code}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Institutional Number</span>
                        <span className="itemValue">
                          {data?.user?.user_banking_details?.institution_number}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Transit / Routing Number</span>
                        <span className="itemValue">
                          {data?.user?.user_banking_details?.transit_number}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Account Number</span>
                        <span className="itemValue">
                          {data?.user?.user_banking_details?.account_number}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Account Holder</span>
                        <span className="itemValue">
                          {data?.user?.user_banking_details?.account_holder}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Order Number</span>
                        <span className="itemValue">{data?.order_id}</span>
                      </div>

                      <div className="detailItem">
                        <span className="itemKey">Payment Method</span>
                        <span className="itemValue">{data?.paymentOption}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="item">
                    <div className="details profile-info">
                      <div className="detailItem">
                        <span className="itemKey">Corporation Name</span>
                        <span className="itemValue">
                          {data?.user?.comp__name}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Investment Account</span>
                        <span className="itemValue">
                          {data?.investmentAccount}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Campaign ID</span>
                        <span className="itemValue">{data?.campaign_id}</span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Campaign Name</span>
                        <span className="itemValue">
                          {data?.campaign_detail?.corporation_legal_name}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Amount</span>
                        <span className="itemValue">{data?.amount}</span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Currency</span>
                        <span className="itemValue">{data?.currency}</span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Bank Country</span>
                        <span className="itemValue">
                          {bankCountryLabel(
                            countries,
                            data?.user?.user_banking_details
                              ?.corporate_bank_country_id
                          )}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Bank Name</span>
                        <span className="itemValue">
                          {
                            data?.user?.user_banking_details
                              ?.corporate_bank_name
                          }
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">IBAN</span>
                        <span className="itemValue">
                          {data?.user?.user_banking_details?.corporate_iban}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">SWIFT Code</span>
                        <span className="itemValue">
                          {
                            data?.user?.user_banking_details
                              ?.corporate_swift_code
                          }
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Institutional Number</span>
                        <span className="itemValue">
                          {
                            data?.user?.user_banking_details
                              ?.corporate_institution_number
                          }
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Transit / Routing Number</span>
                        <span className="itemValue">
                          {
                            data?.user?.user_banking_details
                              ?.corporate_transit_number
                          }
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Account Number</span>
                        <span className="itemValue">
                          {
                            data?.user?.user_banking_details
                              ?.corporate_account_number
                          }
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Account Holder</span>
                        <span className="itemValue">
                          {
                            data?.user?.user_banking_details
                              ?.corporate_account_holder
                          }
                        </span>
                      </div>

                      <div className="detailItem">
                        <span className="itemKey">Order Number</span>
                        <span className="itemValue">{data?.order_id}</span>
                      </div>

                      <div className="detailItem">
                        <span className="itemKey">Payment Method</span>
                        <span className="itemValue">{data?.paymentOption}</span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Payment Action</span>
                        <select
                          className="itemValue dropdown"
                          value={paymentStatus}
                          onChange={(e) => {
                            setPaymentStatus(e.target.value);
                            // updateInvestment(e.target.value);
                            setIsPaymentStatusChanged(true);
                          }}
                          style={{ width: "200px" }}
                        >
                          <option value="PAID">PAID</option>
                          <option value="UNPAID">UNPAID</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="single">
              <div className="singleContainer">
                <div className="top">
                  <div
                    className="left"
                    style={{ textAlign: "left", boxShadow: "unset" }}
                  >
                    <div className="topHeading">
                      {" "}
                      <h1
                        className="title"
                        style={{
                          fontSize: "16px",
                          color: "#333",
                          width: "100%",
                        }}
                      >
                        Update Payment Status & Send Invoice
                      </h1>
                    </div>
                    {/* Invoive Section start */}

                    <>
                      {/* <div className="">
                        <div className="campDashboard-container negativeTopSpace investNow-section">
                          <div className="container"> */}
                      <div className="campDashboard-wrapper investNow-content">
                        <div className="order-confirmation">
                          <div className="invoice-logo-container">
                            <img src={Logo} />
                          </div>
                          <div className="order-confirmation-title">
                            <div className="blue-box"></div>
                            <h1>Order Confirmation</h1>
                          </div>
                          <div className="content">
                            <h2>To</h2>
                            <section className="invoice-details">
                              <div className="to">
                                <h5>
                                  {data?.investmentAccount === "CORPORATE"
                                    ? data?.user?.comp__name
                                    : `${data?.user?.firstname
                                        ?.charAt(0)
                                        .toUpperCase()}${data?.user?.firstname?.slice(
                                        1
                                      )} 
                                            ${data?.user?.lastname
                                              ?.charAt(0)
                                              .toUpperCase()}${data?.user?.lastname?.slice(
                                        1
                                      )}`}
                                </h5>
                                <p>
                                  {console.log("add", data?.user?.firstname)}
                                  {data?.investmentAccount === "CORPORATE"
                                    ? data?.user?.comp_address
                                    : data?.user?.current_address}
                                  ,
                                  <br />
                                  {data?.investmentAccount === "CORPORATE"
                                    ? `${data?.user?.data?.city_name || ""}, 
                                          ${data?.user?.state_name || ""}, 
                                          ${data?.user?.county_name || ""},
                                          ${data?.user?.comp_postal_code || ""}`
                                    : `${data?.user?.city_name || ""}, 
                                          ${data?.user?.state_name || ""}, 
                                          ${data?.user?.county_name || ""}
                                          ${data?.user?.postal_code || ""}`}
                                </p>
                              </div>
                              <div className="invoice-info">
                                <div className="inner-content">
                                  <p>
                                    <strong>Invoice no:</strong>{" "}
                                    <span>{data?.order_id}</span>
                                  </p>
                                  <p>
                                    <strong>Date:</strong>{" "}
                                    <span>{formatDate(data?.dateCreated)}</span>
                                  </p>
                                </div>
                              </div>
                            </section>
                            <table className="order-table">
                              <thead>
                                <tr>
                                  <th>No</th>
                                  <th>Description</th>
                                  <th>Qty</th>
                                  <th>Price/Share</th>
                                  <th>Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td>{data?.order_id}</td>
                                  <td>
                                    {data?.campaign_detail?.security_type}{" "}
                                    issued by{" "}
                                    {
                                      data?.campaign_detail
                                        ?.corporation_legal_name
                                    }
                                  </td>
                                  <td>
                                    {data?.campaign_detail?.issued_shares}
                                  </td>
                                  <td>
                                    {data?.campaign_detail?.price_per_security}
                                  </td>
                                  <td>{data?.amount}</td>
                                </tr>
                              </tbody>
                            </table>
                            <hr className="divider" />
                            <section className="payment-details">
                              <div className="inner-sections">
                                <h2>Payment Method</h2>

                                <div className="checkoutdiv-body">
                                  <p>
                                    {paymentMethodLabel(data?.paymentOption)}
                                  </p>
                                </div>

                                {/* PAYMENT STATUS IS NOT A PROPERTY OF THE RAIL.
                                    This control used to be nested inside the
                                    `paymentMethod == "Wire Transfer"` branch, and
                                    that branch was only ever reachable because
                                    the state it tested was hardcoded to "Wire
                                    Transfer". Deriving the method from the order
                                    without also hoisting this would have taken
                                    the PAID/UNPAID control away from every
                                    Direct Deposit order and every order that has
                                    not reached the payment step — a worse defect
                                    than the wrong label it fixes. */}
                                <div className="checkoutdiv-body">
                                  <p>
                                    <span>
                                      <div className="detailItem">
                                        <span className="itemKey">
                                          Payment Status
                                        </span>
                                        &nbsp;
                                        {isGeneratingPDF || isPdfMode ? ( // Added `isPdfMode` for better control
                                          <span
                                            style={{
                                              color:
                                                paymentStatus === "PAID"
                                                  ? "green"
                                                  : "red",
                                              fontWeight: "bold",
                                            }}
                                          >
                                            {paymentStatus}
                                          </span>
                                        ) : (
                                          <select
                                            className="itemValue dropdown"
                                            value={paymentStatus}
                                            onChange={(e) => {
                                              setPaymentStatus(e.target.value);
                                              setIsPaymentStatusChanged(true);
                                            }}
                                            style={{ width: "200px" }}
                                          >
                                            <option value="PAID">PAID</option>
                                            <option value="UNPAID">
                                              UNPAID
                                            </option>
                                          </select>
                                        )}
                                      </div>
                                    </span>
                                  </p>
                                </div>
                              </div>
                              <div className="inner-sections">
                                <h2>Instructions</h2>
                                <p>
                                  Payment has been received, and you will
                                  receive confirmation within 2 working days. In
                                  case of any issues, please contact{" "}
                                  <i>
                                    <a href="mailto:payments@rootsfundme.ca">
                                      payments@rootsfundme.ca
                                    </a>
                                  </i>
                                </p>
                              </div>
                            </section>
                            <div className="contact-section">
                              <hr className="divider" />
                              <div className="contact-details">
                                <div className="contact-item">
                                  <i className="fa fa-envelope icon"></i>
                                  <div className="contact-text">
                                    <span className="label">Mail</span>
                                    <a
                                      href="mailto:management@rootsfundme.ca"
                                      className="value"
                                    >
                                      management@rootsfundme.ca
                                    </a>
                                  </div>
                                </div>
                                <div className="contact-item">
                                  <i className="fa fa-globe icon"></i>
                                  <div className="contact-text">
                                    <span className="label">Website</span>
                                    <a
                                      href="http://www.rootsfundme.ca"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="value"
                                    >
                                      www.rootsfundme.ca
                                    </a>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        {isPaymentStatusChanged && (
                          <div className="my-5 d-flex justify-content-center">
                            <button
                              type="button"
                              className="custom-btn-outline text-white bg-success"
                              style={{
                                fontSize: "16px",
                                color: "#fff",
                                marginTop: "20px",
                                borderRadius: "15px",
                                width: "10%",
                                marginLeft: "35%",
                                marginRight: "5%",
                                padding: "10px",
                                background: "#4fbd88",
                                alignSelf: "center",
                                minWidth: "150px",
                                textAlign: "center",
                              }}
                              onClick={() => {
                                if (paymentStatus !== "UNPAID") {
                                  setPaymentStatus(paymentStatus);
                                  onSubmit();
                                }
                              }}
                            >
                              Continue
                            </button>
                            <button
                              type="button"
                              className="custom-btn-outline text-white"
                              style={{
                                fontSize: "16px",
                                color: "#fff",
                                marginTop: "10px",
                                borderRadius: "15px",
                                width: "10%",
                                padding: "10px",
                                background: "#dc3545",
                                minWidth: "150px",
                                textAlign: "center",
                              }}
                              onClick={() => window.history.back()}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    </>

                    {/* Invoive Section end */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Invoice Section */}
    </div>
  );
};

export default Viewinvestment;
